export type PaymentNoticeMatchStatus =
  | "MATCHED"
  | "MATCHED_BY_ALIAS"
  | "MATCHED_MANUALLY"
  | "MATCHED_BY_AI"
  | "AMBIGUOUS"
  | "UNMATCHED";

export const PAYMENT_NOTICE_MATCH_STATUS_LABELS: Record<
  PaymentNoticeMatchStatus,
  string
> = {
  MATCHED: "자동 매칭",
  MATCHED_BY_ALIAS: "별칭 매칭",
  MATCHED_MANUALLY: "수동 매칭",
  MATCHED_BY_AI: "AI 매칭",
  AMBIGUOUS: "매칭 실패 (후보 다수)",
  UNMATCHED: "매칭 실패"
};

export type PaymentNoticeDocType =
  | "NATIONAL_TAX"
  | "LOCAL_TAX"
  | "TAX_RECEIPT"
  | "OTHER";

export const PAYMENT_NOTICE_DOC_TYPE_LABELS: Record<
  PaymentNoticeDocType,
  string
> = {
  NATIONAL_TAX: "국세",
  LOCAL_TAX: "지방세",
  // 신고서 접수증(docType: TAX_RECEIPT). 원천세/부가세 구분은 getPaymentNoticeDocTypeLabel 참고
  TAX_RECEIPT: "접수증",
  OTHER: "기타"
};

export interface PaymentNoticeAccount {
  bank: string;
  no: string;
}

// 신고서 접수증(docType: TAX_RECEIPT) 전용 상세 항목 (payment-notice-api.md §3.1).
// 그 외 문서 유형에서는 PaymentNotice.taxReceipt가 null이다.
// 원천세 접수증은 부가세 항목이, 부가세 접수증은 원천세 항목이 null이다.
export interface TaxReceiptInfo {
  receiptNo: string | null;
  receivedAt: string | null;
  receiptResult: string | null;
  reportType: string | null;
  reportCategory: string | null;
  frequency: string | null;
  paymentPeriod: string | null;
  submitPeriod: string | null;
  totalPayAmount: number | null;
  // 소득세 등 납부세액 (PaymentNotice.amount와 동일 값)
  incomeTaxAmount: number | null;
  // 농어촌특별세 납부세액
  ruralTaxAmount: number | null;
  // 차월이월환급세액
  carriedOverRefund: number | null;
  refundRequested: boolean | null;
  // 환급신청금액
  refundRequestAmount: number | null;
  // (부가세) 신고기간 시작일/종료일
  reportPeriodStart?: string | null;
  reportPeriodEnd?: string | null;
  // (부가세) 과세유형 (예: "일반")
  taxationType?: string | null;
  // (부가세) 환급구분 원문 (예: "해당없음", "시설투자환급")
  refundType?: string | null;
  // (부가세) 과세표준
  taxBase?: number | null;
  // (부가세) 차가감납부할세액. 환급이면 음수
  netPayableAmount?: number | null;
  // (부가세) 실제납부할세액. 환급이면 음수 (PaymentNotice.amount와 동일 값)
  actualPayableAmount?: number | null;
}

export interface PaymentNotice {
  id: string;
  organizationId: string;
  businessId: string | null;
  matchStatus: PaymentNoticeMatchStatus;
  matchNote: string | null;
  originalFileName: string;
  contentType: string;
  downloadUrl: string;
  docType: string;
  businessNo: string;
  companyName: string;
  taxItem: string;
  incomeType: string;
  period: string;
  dueDate: string;
  amount: number;
  epayNo: string;
  accounts: PaymentNoticeAccount[];
  confidence: string;
  confidenceReason: string | null;
  // docType이 TAX_RECEIPT인 경우에만 채워짐, 그 외에는 null
  taxReceipt: TaxReceiptInfo | null;
  // 연도/기분 원문 (예: "2026년 2기분"). 부가세/종소세/법인세 상세 조회(§2.11)에서만 내려온다
  yearTerm?: string | null;
  createdDate: string;
}

// 부가세 신고서 접수증은 taxItem이 이 값으로 고정된다 (원천세 접수증은 "원천세")
export const VAT_RECEIPT_TAX_ITEM = "부가가치세(접수증)";

export const isVatReceipt = (notice: Pick<PaymentNotice, "docType" | "taxItem">) =>
  notice.docType === "TAX_RECEIPT" && notice.taxItem === VAT_RECEIPT_TAX_ITEM;

// 문서 종류 라벨. 접수증은 docType만으로 원천세/부가세가 구분되지 않아 taxItem으로 나눈다.
export const getPaymentNoticeDocTypeLabel = (
  notice: Pick<PaymentNotice, "docType" | "taxItem">
) => {
  if (!notice.docType) return "-";
  if (notice.docType === "TAX_RECEIPT")
    return isVatReceipt(notice) ? "부가세 접수증" : "원천세 접수증";
  return (
    PAYMENT_NOTICE_DOC_TYPE_LABELS[notice.docType as PaymentNoticeDocType] ||
    notice.docType
  );
};

// 금액 표기. 접수증은 amount가 납부세액이라 0원이면 "납부할 세액 없음",
// 음수(부가세 환급)면 "환급 ○○원"으로 보여준다.
export const formatPaymentNoticeAmount = (
  notice: Pick<PaymentNotice, "docType" | "amount">
) => {
  if (notice.amount === null || notice.amount === undefined) return "-";
  if (notice.docType === "TAX_RECEIPT") {
    if (notice.amount === 0) return "납부할 세액 없음";
    if (notice.amount < 0)
      return `환급 ${Math.abs(notice.amount).toLocaleString("ko-KR")}원`;
  }
  return `${notice.amount.toLocaleString("ko-KR")}원`;
};

// 부가세/종소세/법인세 납부서 유형 (payment-notice-api.md §1.4, PaymentNoticeTaxType).
// 원천세가 아닌 납부서를 다루는 §2.10~2.13 API의 type 파라미터로 쓴다.
export type OtherTaxType = "VAT" | "GLOBAL_INCOME" | "CORPORATE";

export const OTHER_TAX_TYPES: OtherTaxType[] = [
  "VAT",
  "GLOBAL_INCOME",
  "CORPORATE"
];

export const OTHER_TAX_TYPE_LABELS: Record<OtherTaxType, string> = {
  VAT: "부가세",
  GLOBAL_INCOME: "종소세",
  CORPORATE: "법인세"
};

// 세목으로 납부서 유형을 판별한다 (§1.4 서버 규칙과 동일). 어느 규칙에도 해당하지 않으면 원천세(null).
// 부가세 접수증("부가가치세(접수증)")도 VAT로 분류된다.
export const getOtherTaxType = (
  taxItem: string | null | undefined
): OtherTaxType | null => {
  if (!taxItem) return null;
  if (taxItem.includes("부가가치")) return "VAT";
  if (taxItem.includes("종합소득")) return "GLOBAL_INCOME";
  if (taxItem.includes("법인소득") || taxItem.startsWith("법인세"))
    return "CORPORATE";
  return null;
};

// 기간 항목 라벨. 부가세/종소세/법인세 납부서·접수증은 period가 귀속연월이 아니라 납부연월이다 (§3).
export const getPaymentNoticePeriodLabel = (
  notice: Pick<PaymentNotice, "taxItem">
) => (getOtherTaxType(notice.taxItem) ? "납부연월" : "귀속기간");

export interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
  first: boolean;
  last: boolean;
  empty: boolean;
}

export interface SimpleBusiness {
  id: string;
  name: string;
  regNumber: string;
}

export interface BusinessWithPaymentNoticeCount {
  id: string;
  name: string;
  regNumber: string;
  incharge?: string;
  cluster?: string;
  subCluster?: string;
  // 사업자 소유 기관 대표 사용자(User)의 휴대전화번호. 읽기 전용
  userMobileNo?: string | null;
  // Business.payrollManagerMobileNo (급여담당자 연락처). 카카오 알림톡 발신 우선 번호로 쓰이며,
  // PRO에서 쓰기 가능한 필드는 이것뿐. (구 Business.kakaoMobileNo 필드는 제거됨)
  kakaoMobileNo?: string | null;
  // 조회한 귀속연월(period)에 대한 카카오 알림톡 발송 일시
  kakaoSentAt?: string | null;
  nationalTaxCount: number;
  localTaxCount: number;
  // 원천세 신고서 접수증(docType: TAX_RECEIPT) 건수. totalCount에도 합산됨
  taxReceiptCount: number;
  otherCount: number;
  totalCount: number;
}

export interface BusinessWithPaymentNotices {
  id: string;
  name: string;
  regNumber: string;
  paymentNotices: PaymentNotice[];
  // 조회 period(원천세는 귀속월 변환)에 대한 가장 최근 알림톡 발송 일시. 발송 이력이 없으면 미포함
  kakaoSentAt?: string | null;
}

// 2.8 납부서 카카오 알림톡 발송 이력 1건 (Business.kakaoHistory[period] 요소, 발송 순서대로)
export interface KakaoNotificationLog {
  sentAt: string;
  // 사용한 알림톡 템플릿 ID (KA01TP...)
  templateId: string;
  // 실제 발송한 수신 번호 (발신 번호 우선순위로 해석된 최종 번호)
  recipientMobileNo: string | null;
  // 템플릿 치환 변수. 키는 "#{BNM}" 처럼 중괄호를 포함한 형태
  variables: Record<string, string>;
}

// 파일 1개당 업로드 API 호출 결과 - 성공 시 생성된 PaymentNotice, 실패 시 에러 메시지
export interface PaymentNoticeUploadResult {
  fileName: string;
  notice: PaymentNotice | null;
  errorMessage?: string;
  // 업로드가 실제로 시도조차 되지 않고 중단(취소)된 건인지 여부 - 실패(errorMessage)와
  // 구분해서 집계/표시하기 위한 플래그
  cancelled?: boolean;
}

// 수동 매칭 확정 요청 1건
export interface PaymentNoticeMatchRequest {
  paymentNoticeId: string;
  businessId: string;
}

// 수동 매칭 확정 응답 1건 - 요청 배열과 1:1 순서 매핑되지 않으므로 paymentNoticeId로 건을 식별하고,
// 건별로 성공 여부가 갈린다 (payment-notice-api.md §2.3)
export interface PaymentNoticeMatchResult {
  paymentNoticeId: string;
  success: boolean;
  paymentNotice: PaymentNotice | null;
  errorMessage: string | null;
}
